import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { GuideClosing } from "@/components/guides/GuideClosing";
import { LandingNav } from "@/components/landing/LandingNav";
import { Container, ContourField, Eyebrow } from "@/components/landing/primitives";
import { FREE_TOOL_KEYS, getGuide, guideNavPath } from "@/lib/guides";
import { isLandingLocale, localePath, makeTr, pick } from "@/lib/landing/locale";
import { languageFromPath } from "@/lib/site/publicPages";

/**
 * The free tools hub ("Nástroje zdarma"): every browser-only tool with what it
 * does in one line. The privacy line is the point: nothing is uploaded and no
 * account is needed, so it says so once for all of them and again on each card.
 */
export default function Tools() {
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const locale = isLandingLocale(lang) ? lang : (languageFromPath(pathname) ?? "cs");
  const tr = makeTr(locale);
  const navPath = guideNavPath("tools");

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <LandingNav locale={locale} path={navPath} />

      <main id="obsah">
        <section aria-labelledby="tools-title" className="relative isolate overflow-hidden">
          <ContourField variant="hero" className="opacity-[0.14] dark:opacity-[0.08]" />
          <Container className="pb-10 pt-14 sm:pb-14 sm:pt-20">
            <Eyebrow className="mb-3">{tr("Nástroje zdarma", "Free tools")}</Eyebrow>
            <h1
              id="tools-title"
              className="max-w-3xl text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl"
            >
              {tr(
                "Nástroje zdarma pro bankovní výpisy a účetnictví",
                "Free tools for bank statements and bookkeeping"
              )}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {tr(
                "Všechny běží jen ve vašem prohlížeči: soubor se nikam neodesílá, nic se neukládá a nepotřebujete registraci.",
                "They all run only in your browser: the file is never sent anywhere, nothing is stored and no sign-up is needed."
              )}
            </p>
          </Container>
        </section>

        <Container className="pb-20 sm:pb-28">
          <ul className="grid gap-4 md:grid-cols-2">
            {FREE_TOOL_KEYS.map((key) => {
              const tool = getGuide(key);
              return (
                <li key={key}>
                  <Link
                    to={localePath(locale, guideNavPath(key))}
                    className="group flex h-full items-start justify-between gap-4 rounded-xl border bg-card p-5 transition-colors hover:bg-secondary/50"
                  >
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight">
                        {pick(tool.name, locale)}
                      </h2>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">
                        {tool.freeTool
                          ? pick(tool.freeTool.summary, locale)
                          : pick(tool.lead, locale)}
                      </p>
                      <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <ShieldCheck
                          aria-hidden="true"
                          className="size-3.5 text-[hsl(var(--action))] dark:text-primary"
                        />
                        {tr("Běží ve vašem prohlížeči", "Runs in your browser")}
                      </p>
                    </div>
                    <ArrowRight
                      aria-hidden="true"
                      className="mt-1.5 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Container>
      </main>

      <GuideClosing locale={locale} path={navPath} />
    </div>
  );
}
